import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import Button from '@atlaskit/button/standard-button';
import InviteTeamIcon from '@atlaskit/icon/glyph/invite-team';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import {
	fireCoreInvitesInJsmProjectFeatureExposed,
	fireCoreInvitesInJiraProjectFeatureExposed,
} from '@atlassian/jira-core-invites-in-projects/src/common/utils/analytics';
import {
	isCoreInvitesInJiraProjectsEnabled,
	isElligibleProject,
	isServiceProject,
} from '@atlassian/jira-core-invites-in-projects/src/common/utils/index.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ProjectAddPeopleAppAsync from '@atlassian/jira-project-add-people/src/async.tsx';
import { isReferrerNPLEPage } from '@atlassian/jira-project-add-people/src/common/utils';
import InvitePeopleModalAsync from '@atlassian/jira-project-invite-people-modal/async';
import { AssigneeFilterAddPeopleButtonWrapperNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/assignee-filter-add-people/async.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UpFlowTouchpoint from '@atlassian/jira-up-flow-touchpoint/src/main.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import type { AccessPageContext } from '../../../common/types';
import MainProviderContext from '../../../services/access-page-context';
import { addPeopleMetrics } from '../../../utils/performance-analytics';
import messages from './messages';
import type { Props } from './types';

export const useMainProviderContext = () => useContext(MainProviderContext);
export const AddPeople = (props: Props) => {
	const {
		appEdition,
		baseUrl,
		projectId,
		projectKey,
		projectType,
		projectName,
		isAdmin,
		isProjectSimplified,
	}: AccessPageContext = useMainProviderContext();
	const {
		hasPermissionsGate,
		isFetching,
		isError,
		defaultRoleId, // this should come from the actors service api - DWP-69
		projectRoles, // this should come from the actors service api - DWP-69
		intl: { formatMessage },
		accessType,
		onAddActorSuccess,
	} = props;
	const { cloudId, isSiteAdmin } = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isTouchpointOpen, setIsTouchpointOpen] = useState<boolean>(false);
	const onTouchpointOpen = useCallback((): void => {
		setIsTouchpointOpen(true);
	}, [setIsTouchpointOpen]);
	const onTouchpointClose = useCallback((): void => {
		setIsTouchpointOpen(false);
	}, [setIsTouchpointOpen]);

	const [isAddPeopleDialogOpen, setIsAddPeopleDialogOpen] = useState<boolean>(false);
	const [hasDialogBeenOpened, setHasDialogBeenOpened] = useState<boolean>(false);
	const [{ location }, { replace }] = useRouter();

	const onOpenAddPeopleDialog = useCallback((): void => {
		if (isElligibleProject(projectType)) {
			fireCoreInvitesInJiraProjectFeatureExposed(createAnalyticsEvent, isSiteAdmin);
		}
		if (isServiceProject(projectType)) {
			const coreInvitesJsmExpValue = expVal(
				'jsm_core_invites_experiment',
				'cohort',
				'not-enrolled',
			);
			fireCoreInvitesInJsmProjectFeatureExposed(createAnalyticsEvent, coreInvitesJsmExpValue);
		}
		setIsAddPeopleDialogOpen(true);
		setHasDialogBeenOpened(true);
		addPeopleMetrics.start();
	}, [createAnalyticsEvent, isSiteAdmin, projectType]);

	useEffect(() => {
		if (isReferrerNPLEPage()) {
			onOpenAddPeopleDialog();
		}
	}, [onOpenAddPeopleDialog]);

	const onStopPerfMetric = () => addPeopleMetrics.stop();

	const onCloseAddPeopleDialog = useCallback((): void => {
		setIsAddPeopleDialogOpen(false);
		// making the query param empty after closing the modal if user came from nple page on clicking Add people button
		if (isReferrerNPLEPage()) {
			replace({ ...location, search: '' });
		}
	}, [setIsAddPeopleDialogOpen, replace, location]);

	const renderAddPeopleButtonOnly = useCallback(
		(isDisabled: boolean) => (
			<Button
				// @ts-expect-error - TS2322 - Type '{ children: string; externalId: string; appearance: "primary"; onClick: () => void; isDisabled: boolean; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
				externalId="users-and-roles.open-add-people-dialog-button"
				appearance="primary"
				onClick={onOpenAddPeopleDialog}
				isDisabled={isFetching || isError || isDisabled}
				iconBefore={
					isServiceProject(projectType) &&
					expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment') ? (
						<InviteTeamIcon size="medium" label="" />
					) : undefined
				}
			>
				{formatMessage(messages.addPeople)}
			</Button>
		),
		[formatMessage, isError, isFetching, onOpenAddPeopleDialog, projectType],
	);
	const renderAddPeopleButton = useCallback(
		(isDisabled: boolean) => (
			<div
				data-pollinator-users-and-roles-add-people-button
				data-testid="users-and-roles.ui.header.add-people.button"
			>
				<AssigneeFilterAddPeopleButtonWrapperNudgeAsync>
					{renderAddPeopleButtonOnly(isDisabled)}
				</AssigneeFilterAddPeopleButtonWrapperNudgeAsync>
			</div>
		),
		[renderAddPeopleButtonOnly],
	);

	const getFreeMessageBasedOnProjectType = (): MessageDescriptor => {
		if (projectType === SERVICE_DESK_PROJECT) {
			return messages.addPeopleJsmFreeTooltip;
		}
		return messages.addPeopleJswFreeTooltip;
	};
	const freeText = formatMessage(getFreeMessageBasedOnProjectType());

	let addPeopleButton;
	if (isProjectSimplified) {
		addPeopleButton = renderAddPeopleButton(false);
	} else {
		addPeopleButton = (
			<UpFlowTouchpoint
				isOpen={hasPermissionsGate && isTouchpointOpen}
				actionSubjectId="permissionGatedAddPeople"
				feature="permission"
				touchpointId="projectSettingsAccessPermissionGatedAddPeople"
				internationalizedMessage={freeText}
				placement="bottom-end"
				onOpen={onTouchpointOpen}
				onClose={onTouchpointClose}
			>
				{renderAddPeopleButton(hasPermissionsGate)}
			</UpFlowTouchpoint>
		);
	}

	const addPeopleModal = useMemo(() => {
		const onAddActorSuccessWithFireTrack = () => {
			onAddActorSuccess();
		};
		if (
			(isCoreInvitesInJiraProjectsEnabled(isSiteAdmin) && isElligibleProject(projectType)) ||
			(isServiceProject(projectType) &&
				expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment'))
		) {
			return (
				<InvitePeopleModalAsync
					appEdition={appEdition}
					cloudId={cloudId}
					isOpen={isAddPeopleDialogOpen}
					onClose={onCloseAddPeopleDialog}
					jiraProjectName={projectName}
					jiraProjectKey={projectKey}
					projectId={projectId}
					defaultRoleId={defaultRoleId}
					isSiteAdmin={isSiteAdmin}
					onAddSuccess={onAddActorSuccessWithFireTrack}
				/>
			);
		}
		return (
			<ProjectAddPeopleAppAsync
				baseUrl={baseUrl}
				projectId={projectId}
				projectKey={projectKey}
				projectType={projectType}
				isAdmin={isAdmin}
				analyticsPrefix="users-and-roles"
				projectRoles={projectRoles}
				defaultRoleId={defaultRoleId}
				isOpen={isAddPeopleDialogOpen}
				onAddSuccess={onAddActorSuccessWithFireTrack}
				onClose={onCloseAddPeopleDialog}
				onError={onCloseAddPeopleDialog}
				allowFlags
				accessLevel={accessType ? accessType.displayName : ''}
				onStopPerfMetric={onStopPerfMetric}
			/>
		);
	}, [
		isSiteAdmin,
		projectType,
		baseUrl,
		projectId,
		projectKey,
		isAdmin,
		projectRoles,
		defaultRoleId,
		isAddPeopleDialogOpen,
		onCloseAddPeopleDialog,
		accessType,
		onAddActorSuccess,
		appEdition,
		cloudId,
		projectName,
	]);

	return (
		<>
			{addPeopleButton}
			{hasDialogBeenOpened && addPeopleModal}
		</>
	);
};

export default injectIntl(AddPeople);
